import { Box, Divider, Fade, useTheme } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import ChatRequests from '@src/components/ChatRequests';
import ChatsBot from '@src/components/ChatsBot';
import ChatsOngoing from '@src/components/ChatsOngoing';
import usePrevious from '@src/hooks/usePrevius';
import {
  activeChatSideSelector,
  isChatsBotLoadingSelector,
  isChatsOnHoldLoadingSelector,
  isChatsRequestLoadingSelector,
  totalChatsBotSelector,
  totalChatsOnHoldSelector,
  totalChatsRequestSelector,
} from '@src/selectors/chats';
import { ChatSideType } from '@src/types/chats';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const FIRST_LOADING_MS = 1300;
const LOADING_MS = 600;

const isFirstLoadingSideContext: Record<ChatSideType, boolean> = {
  [ChatSideType.ON_HOLD]: false,
  [ChatSideType.REQUEST]: false,
  [ChatSideType.BOT]: false,
};

const ChatTraySection: React.FC<{
  chatsSearchRef: React.RefObject<HTMLDivElement>;
}> = ({ chatsSearchRef }) => {
  const theme = useTheme();
  const activeChatSide = useSelector(activeChatSideSelector);
  const prevActiveChatSide = usePrevious(activeChatSide, 2000);

  const totalChatsBot = useSelector(totalChatsBotSelector);
  const totalChatsRequest = useSelector(totalChatsRequestSelector);
  const totalChatsOnHold = useSelector(totalChatsOnHoldSelector);

  const prevTotalChatsBot = usePrevious(totalChatsBot);
  const prevTotalChatsRequest = usePrevious(totalChatsRequest);
  const prevTotalChatsOnHold = usePrevious(totalChatsOnHold);

  const [isLoading, setIsLoading] = useState(false);
  const isChatsBotLoading = useSelector(isChatsBotLoadingSelector);
  const isChatsRequestLoading = useSelector(isChatsRequestLoadingSelector);
  const isChatsOnHoldLoading = useSelector(isChatsOnHoldLoadingSelector);

  useEffect(() => {
    if (
      prevActiveChatSide !== activeChatSide ||
      (activeChatSide === ChatSideType.BOT &&
        totalChatsBot !== prevTotalChatsBot) ||
      (activeChatSide === ChatSideType.REQUEST &&
        totalChatsRequest !== prevTotalChatsRequest) ||
      (activeChatSide === ChatSideType.ON_HOLD &&
        totalChatsOnHold !== prevTotalChatsOnHold)
    ) {
      setIsLoading(true);

      const hasLoadedBefore = isFirstLoadingSideContext[activeChatSide];
      const delay = hasLoadedBefore ? LOADING_MS : FIRST_LOADING_MS;

      const t = setTimeout(() => {
        isFirstLoadingSideContext[activeChatSide] = true;
        setIsLoading(false);
      }, delay);

      return () => clearTimeout(t);
    }

    if (isLoading) setIsLoading(false);

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChatSide, totalChatsBot, totalChatsRequest, totalChatsOnHold]);

  const isLoadingSection =
    isLoading ||
    (activeChatSide === ChatSideType.BOT && isChatsBotLoading) ||
    (activeChatSide === ChatSideType.REQUEST && isChatsRequestLoading) ||
    (activeChatSide === ChatSideType.ON_HOLD && isChatsOnHoldLoading);

  return (
    <Box position="relative" width="100%" height="100%">
      <Fade in={prevActiveChatSide !== activeChatSide && isLoadingSection}>
        <Box
          pt={0.5}
          position="absolute"
          width="100%"
          height="100%"
          left={0}
          top={0}
          zIndex={3}
          style={{ backgroundColor: theme.palette.background.paper }}
        >
          {Array.from({ length: 8 }, (_, index) => (
            <Box px={2} key={index} height={75}>
              <Box display="flex" gridGap={8} py={2}>
                <Skeleton variant="circle" width={40} height={40} />
                <Box width="calc(100% - 50px)">
                  <Skeleton height={18} width="80%" />
                  <Skeleton height={15} width="40%" />
                  <Box display="flex" justifyContent="flex-end" mt={-1}>
                    <Skeleton height={15} width="20%" />
                  </Box>
                </Box>
              </Box>

              <Box my={0.5}>
                <Divider
                  style={{ color: theme.b2chatgray['300'], opacity: 0.4 }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Fade>

      {activeChatSide === ChatSideType.ON_HOLD && (
        <ChatsOngoing chatsSearchRef={chatsSearchRef} />
      )}
      {activeChatSide === ChatSideType.REQUEST && (
        <ChatRequests chatsSearchRef={chatsSearchRef} />
      )}
      {activeChatSide === ChatSideType.BOT && (
        <ChatsBot chatsSearchRef={chatsSearchRef} />
      )}
    </Box>
  );
};

export default ChatTraySection;
